import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Droplets, Camera, FolderOpen, Maximize2, Minimize2, MapPin } from 'lucide-react';
import MoistAreas from './MoistAreas';
import { compressImage, uid } from '../lib/model';
import { MO_UNITS, seriesOf, areasOf } from '../lib/moist';
import { mo2 } from '../lib/i18nMoist2';
import { useMoistPins } from '../hooks/useMoistPins';
import { portalRoot } from './TourGuide';

export const openMoist = (id) => window.dispatchEvent(new CustomEvent('giss-moist', { detail: id }));
const dims = (src) => new Promise((res) => { const i = new Image(); i.onload = () => res([i.naturalWidth, i.naturalHeight]); i.onerror = () => res([4, 3]); i.src = src; });

// moisture distribution on a wall: photo -> grid -> one value per cell -> colour map; follow-ups share the wall
export default function MoistGrid({ t, project, updateProject }) {
  const [on, setOn] = useState(false);
  const [cur, setCur] = useState(null);
  const [full, setFull] = useState(false);
  const [pick, setPick] = useState(null);
  const file = useRef(null), cam = useRef(null), fol = useRef(null);
  useEffect(() => { const h = (e) => { setOn(true); if (typeof e.detail === 'string') setCur(e.detail); }; window.addEventListener('giss-moist', h); return () => window.removeEventListener('giss-moist', h); }, []);
  useMoistPins(project, pick);
  // place mode: dialog hidden, next map tap sets the position of the whole wall series
  useEffect(() => {
    const m = pick && window.__gissMapView?.()?.m;
    if (!m) return undefined;
    const tap = (e) => {
      const key = pick, pos = { lat: e.latlng.lat, lon: e.latlng.lng, floor: project.planOn || null };
      updateProject((pr) => { const base = (pr.moist || []).find((x) => x.id === key); return { ...pr, moist: (pr.moist || []).map((x) => (base && (x.wall || x.id) === (base.wall || base.id) ? { ...x, pos } : x)) }; });
      toast.success(mo2().placed); setPick(null); setOn(true);
    };
    m.once('click', tap);
    return () => { m.off('click', tap); };
  }, [pick]); // eslint-disable-line react-hooks/exhaustive-deps
  const L2 = mo2();
  if (pick) return createPortal(<div className="mo-pick" data-testid="moist-place-banner"><MapPin size={16} /> {L2.placeHint}<button className="btn btn-outline btn-sm" data-testid="moist-place-cancel" onClick={() => { setPick(null); setOn(true); }}>{L2.cancel}</button></div>, document.body);
  if (!on) return null;
  const list = project.moist || [];
  const m = list.find((x) => x.id === cur);
  const set = (id, p) => updateProject((pr) => ({ ...pr, moist: (pr.moist || []).map((x) => (x.id === id ? { ...x, ...p } : x)) }));
  const add = async (f, base) => {
    if (!f) return;
    try {
      const src = (await compressImage(f, 1600, 0.82)).data, [w, h] = await dims(src);
      const rows = w >= h ? 5 : 8, cols = w >= h ? 8 : 5, s = base && seriesOf(list, base);
      if (base) base = s[s.length - 1];
      const it = base
        ? { ...base, id: uid('mo'), wall: base.wall || base.id, name: `${(base.name || '').replace(/ – .*$/, '')} – ${L2.meas} ${s.length + 1}`, src, w, h, at: Date.now(), areas: areasOf(base).map((a) => ({ ...a, vals: {} })) }
        : { id: uid('mo'), name: `${t.moWall} ${list.length + 1}`, src, w, h, areas: [], unit: 'digits', ...MO_UNITS.digits, op: 0.55, floor: project.planOn || null, at: Date.now(), rows, cols };
      delete it.u;
      updateProject((pr) => ({ ...pr, moist: [...(pr.moist || []), it] }));
      setCur(it.id);
      if (base) toast.message(L2.followHint);
    } catch { toast.error(t.fpFail); }
  };
  return createPortal(
    <div className="editor-backdrop" data-testid="moist-dialog">
      <div className={`editor irr-bom ${full ? 'ed-full' : ''}`}>
        <div className="editor-head"><h4><Droplets size={18} /> {t.moTitle}</h4><button className="icon-btn" data-testid="moist-full-btn" onClick={() => setFull(!full)}>{full ? <Minimize2 size={16} /> : <Maximize2 size={16} />}</button><button className="icon-btn" data-testid="moist-close-btn" onClick={() => setOn(false)}><X size={16} /></button></div>
        <div className="row">
          {list.map((x) => <button key={x.id} className={`chip seg ${cur === x.id ? 'on' : ''}`} data-testid={`moist-tab-${x.id}`} onClick={() => setCur(x.id)}>{x.name}</button>)}
          <button className="btn btn-green btn-sm" data-testid="moist-add-btn" onClick={() => cam.current.click()}><Camera size={14} /> {t.moAdd}</button>
          <button className="btn btn-outline btn-sm" data-testid="moist-file-btn" onClick={() => file.current.click()}><FolderOpen size={14} /> {t.moFile}</button>
          <input ref={cam} type="file" hidden accept="image/*" capture="environment" data-testid="moist-cam-input" onChange={(e) => { add(e.target.files[0]); e.target.value = ''; }} />
          <input ref={file} type="file" hidden accept="image/*" data-testid="moist-file-input" onChange={(e) => { add(e.target.files[0]); e.target.value = ''; }} />
          <input ref={fol} type="file" hidden accept="image/*" capture="environment" data-testid="moist-follow-input" onChange={(e) => { add(e.target.files[0], m); e.target.value = ''; }} />
        </div>
        {!m && <p className="empty" data-testid="moist-empty">{t.moEmpty}</p>}
        {m && <MoistAreas key={m.id} t={t} m={m} series={seriesOf(list, m)} onFollow={() => fol.current.click()} onPlace={() => { setOn(false); setPick(m.id); }} set={(p) => set(m.id, p)} del={() => { if (!window.confirm(t.moDelQ)) return; updateProject((pr) => ({ ...pr, moist: (pr.moist || []).filter((x) => x.id !== m.id) })); setCur(null); }} />}
      </div>
    </div>,
    portalRoot(),
  );
}
